// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { z } from 'zod';

const serialValidationItemResponseSchema = z.object({
  productId: z.string().uuid(),
  validSerials: z.array(z.string()),
  invalidSerials: z.array(z.string()),
});

const validateSerialsResponseSchema = z.object({
  data: z.object({
    items: z.array(serialValidationItemResponseSchema),
  }),
  metadata: responseMetadataSchema,
});

interface ValidateSerialsRequest {
  items: {
    productId: string;
    serials: string[];
  }[];
  /**
   * When true, serials must already exist in the system (for return orders).
   * When false (default), serials must NOT exist (for purchase orders creating new assets).
   */
  requireExisting?: boolean;
}

type ValidateSerialsResponse = z.infer<
  typeof validateSerialsResponseSchema
>['data'];

/**
 * Validate serials for products before receiving a shipment.
 *
 * @param request - The validation request containing product IDs and their serials
 * @returns The validation results with valid and invalid serials for each product
 */
export async function validateSerials(
  request: ValidateSerialsRequest
): Promise<ValidateSerialsResponse> {
  const url = '/v1/shipment-tools/validate-serials';
  const response = await api.post(url, request);

  const { data } = await parseResponse(
    validateSerialsResponseSchema,
    response.data,
    {
      schemaName: 'ValidateSerials',
      endpoint: url,
    }
  );
  return data;
}
